/**
 * The preview worker, owned once per participant session (Frame renders this). A screen that wants the preview calls `usePreviewFeed(draft)`:
 * it hands over the draft it shows (the editor's, or the saved one) and reads the shared state. The context loads with the round; nothing runs until a consumer has fed a draft,
 * and the run follows (the round's preview context, the fed draft) exactly as a page-local preview did: the next edit overtakes a pending result.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { DraftView } from "@bsim/contracts";
import type { PreviewInput, PreviewTrace } from "@bsim/domain";
import { useQueryClient } from "@tanstack/react-query";
import { useRoute } from "../api/context";
import { createPreviewWorker, type PreviewWatch, type PreviewWorkerClient } from "../preview-worker";
import { usePreview, type PreviewState, type PreviewSteps } from "../decisions/usePreview";

export interface PreviewFeed {
  readonly state: PreviewState;
  readonly watch: PreviewWatch | null;
  /** What each changed decision did (null until the Worker posts it for the current run, and when the build has no trace engine). */
  readonly trace: PreviewTrace | null;
  /** The confirmed draft the run is on (what the server holds, never an unsaved or refused edit: OR-30.76, the preview prices only the saved draft); null while no screen feeds one. */
  readonly draft: DraftView | null;
  /** The probed decision's step one option each way on the current run (the drivers panel's options table); null until the Worker posts them. */
  readonly steps: PreviewSteps | null;
  /** The round's preview context, once loaded (its anchors name the risky choices). */
  readonly context: PreviewInput | undefined;
  /** Re-runs the preview, and re-reads the context first when that is what failed. */
  readonly retry: () => void;
  readonly follow: (draft: DraftView | null, valueLegs?: boolean, pending?: boolean) => void;
  /** Asks for the step one option each way of a setting (its leaf path); the returned function withdraws the ask. The latest ask standing is the one the Worker steps. */
  readonly stepOf: (leafPath: string) => () => void;
}

let nextAsk = 0;
const PreviewContext = createContext<PreviewFeed | null>(null);

export function PreviewProvider({ teamId, roundId, createWorker = createPreviewWorker, children }: {
  readonly teamId: string | null; readonly roundId: string | null; readonly createWorker?: (() => PreviewWorkerClient) | undefined; readonly children: ReactNode;
}) {
  const [feeding, setFeeding] = useState<{ readonly draft: DraftView | null; readonly valueLegs: boolean; readonly pending: boolean }>({ draft: null, valueLegs: false, pending: false });
  const fed = feeding.draft;
  const follow = useCallback((draft: DraftView | null, valueLegs = false, pending = false) => setFeeding((f) => (f.draft === draft && f.valueLegs === valueLegs && f.pending === pending ? f : { draft, valueLegs, pending })), []);
  const [asks, setAsks] = useState<readonly { readonly key: number; readonly leaf: string }[]>([]);
  const stepOf = useCallback((leaf: string) => {
    const key = nextAsk++;
    setAsks((a) => [...a, { key, leaf }]);
    return () => setAsks((a) => a.filter((x) => x.key !== key));
  }, []);
  const probe = asks.at(-1)?.leaf ?? null;
  const worker = useMemo(createWorker, [createWorker]);
  // The context loads as soon as the round is known, so a screen that feeds a draft later finds it waiting; the run itself waits for a consumer.
  const context = useRoute("getPreviewContext", teamId === null || roundId === null ? null : { teamId, roundId });
  const run = usePreview(worker, fed === null ? undefined : context.data, fed, feeding.valueLegs, probe);
  const failed = context.error !== null;
  // A result belongs to the context it ran on: while the round's context is a newer one (a card was answered, here or by a teammate) the point on screen is old, so it reads "Updating" and never "Fresh".
  // Keyed on the context changing or having been invalidated (a card answered here: the refetch is still in flight and the data is the old object), not on any fetch in flight, so background polling does not flip the badge.
  const queryClient = useQueryClient();
  const invalidated = teamId !== null && roundId !== null && queryClient.getQueryState(["getPreviewContext", { teamId, roundId }])?.isInvalidated === true;
  const refreshing = context.data !== undefined && run.basis !== undefined && (run.basis !== context.data || invalidated) && run.state.status === "ready";
  // One object per situation, never a fresh one per render: the feed below changes with `state`, and a feed that changes on every render re-renders its consumer, whose new draft is fed back here (an update loop while refreshing).
  const state = useMemo<PreviewState>(() => failed ? { status: "error" } : (refreshing || (feeding.pending && run.state.status === "ready")) ? { status: "computing", previous: run.state.status === "ready" ? run.state.result : null } : run.state, [failed, refreshing, feeding.pending, run.state]);
  const feed = useMemo<PreviewFeed>(() => ({
    state, watch: run.watch, trace: run.trace, steps: run.steps, draft: fed, context: context.data, follow, stepOf,
    retry: () => { run.retry(); if (failed) void context.refetch(); },
  }), [state, run.watch, run.trace, run.steps, fed, run.retry, context.data, context.refetch]);
  return <PreviewContext.Provider value={feed}>{children}</PreviewContext.Provider>;
}

/** True under a `PreviewProvider`. */
export const useHasPreview = (): boolean => useContext(PreviewContext) !== null;

/** The round's preview context (the own bank's round-start state and the content parameters the rules read), when a provider holds it; a screen outside the participant frame gets nothing. */
export const usePreviewContext = (): PreviewInput | undefined => useContext(PreviewContext)?.context;

/** The shared preview as it stands, without feeding a draft: null outside a provider. For a panel that only reads what the page already works out (the drivers panel on a decision's sheet). */
export const usePreviewRead = (): Pick<PreviewFeed, "state" | "trace" | "draft" | "steps" | "stepOf"> | null => useContext(PreviewContext);

/**
 * The shared preview for the CONFIRMED draft (what the server holds); the provider idles again when the consumer unmounts. `pending` is true while decision edits are on their way to the server:
 * the point on screen is then the saved plan's, and every reader sees "Updating" until the save lands and the run on it follows.
 */
export function usePreviewFeed(confirmed: DraftView, valueLegs = false, pending = false): Omit<PreviewFeed, "follow"> {
  const feed = useContext(PreviewContext);
  if (feed === null) throw new Error("usePreviewFeed needs a PreviewProvider");
  const { follow } = feed;
  useEffect(() => follow(confirmed, valueLegs, pending), [follow, confirmed, valueLegs, pending]);
  useEffect(() => () => follow(null), [follow]);
  return feed;
}
